import TileActionResult from 'app/src/engine/action-results/TileActionResult';
import ConveyorTileInteraction from 'app/src/engine/tile-interactions/ConveyorTileInteraction';
import TileInteraction from 'app/src/engine/tile-interactions/TileInteraction';
import ConveyorTile, {
	ConveyorTileArrow,
	getConveyorTileArrow,
} from 'app/src/engine/tiles/ConveyorTile';
import Tile from 'app/src/engine/tiles/Tile';
import ConveyorTileNode from 'app/src/game/map/tiles/ConveyorTile';
import GamePlugin, { TileData } from 'app/src/plugins/GamePlugin';
import * as React from 'react';

export enum ConveyorsGamePluginError {
	MissingArrowInTileDescription,
}

export default class ConveyorsGamePlugin extends GamePlugin {
	private _tileInteraction = new ConveyorTileInteraction();

	// Get a list of Tiles that this plugin provides. When the tile grid is
	// being initialized, it'll ask all plugins which Tiles they create, and
	// later use those corresponding plugins to build Tiles.
	public getListOfProvidedTiles(): string[] {
		return ['ConveyorTile'];
	}

	// Based on Tile data, generate a Tile.
	public getTile(data: TileData): Tile {
		const arrowName = data['arrow'];
		if (typeof arrowName !== 'string') {
			throw ConveyorsGamePluginError.MissingArrowInTileDescription;
		}
		const arrow = getConveyorTileArrow(arrowName);

		const tile = new ConveyorTile(arrow);

		const wallsData = data['walls'];
		if (Array.isArray(wallsData)) {
			tile.addWallsFromData(wallsData);
		}

		return tile;
	}

	// If the editor chooses to support this plugin, and if this plugin displays
	// tiles, this list of tiles will be used in the side panel.
	public getCanonicalTiles(): Tile[] {
		return [
			new ConveyorTile(ConveyorTileArrow.Up),
			new ConveyorTile(ConveyorTileArrow.UpAndFromLeft),
			new ConveyorTile(ConveyorTileArrow.UpAndFromRight),
			new ConveyorTile(ConveyorTileArrow.LeftFromBottom),
			new ConveyorTile(ConveyorTileArrow.RightFromBottom),
			new ConveyorTile(ConveyorTileArrow.MergeUp),
		];
	}

	// Get a list of tile interaction plugins that this plugin provides.
	public getTileInteractions(): TileInteraction[] {
		return [this._tileInteraction];
	}

	// Given a Tile, is this plugin capable of generating a corresponding TileNode?
	public providesNodeForTile(tile: Tile): boolean {
		return tile instanceof ConveyorTile;
	}

	// Based on a Tile, generate a TileNode.
	public getTileNode(
		tile: Tile,
		row: number,
		col: number,
		actionResults?: TileActionResult[] | null,
		completeAnimation?: () => void,
	): React.ReactElement {
		if (!(tile instanceof ConveyorTile)) {
			throw new Error('Unsupported Tile.');
		}

		return (
			<ConveyorTileNode
				arrow={tile.arrow}
				row={row}
				col={col}
				actionResults={actionResults}
				completeAnimation={completeAnimation}
			/>
		);
	}
}
